import { picturePuzzleView } from '../../../packages/domain/src/61-picture-puzzles.mjs';

const SVG_NS = 'http://www.w3.org/2000/svg';
function svgNode(ctx, tag, attributes) {
  const node = globalThis.document?.createElementNS
    ? document.createElementNS(SVG_NS, tag)
    : ctx.e(tag);
  for (const [key, value] of Object.entries(attributes)) node.setAttribute(key, String(value));
  return node;
}

/** Every fragment uses one common full-image coordinate system. Contain preserves
 * the whole original; cover applies the same centred crop to every fragment.
 * Only visible tile values select crops; the completed blank reveals its own
 * final picture region and never displays a fictitious numbered tile. */
export function pictureTileArtwork(ctx, view, cell, picture, fit = 'contain') {
  const art = svgNode(ctx, 'svg', {
    class: 'picture-fragment',
    viewBox: `${(((cell.value || view.cells.length) - 1) % view.columns) * 100} ${Math.floor(((cell.value || view.cells.length) - 1) / view.columns) * 100} 100 100`,
    'aria-hidden': 'true',
    focusable: 'false',
  });
  if (cell.value || view.completed)
    art.append(
      svgNode(ctx, 'image', {
        href: picture.url,
        x: 0,
        y: 0,
        width: view.columns * 100,
        height: view.rows * 100,
        preserveAspectRatio: fit === 'cover' ? 'xMidYMid slice' : 'xMidYMid meet',
      }),
    );
  return art;
}

/** Keyed native cells retain focus and fixed geometry while policy owns legal
 * moves. Private image URLs live only in this ephemeral presentation surface. */
export function paintPictureBoard(
  ctx,
  board,
  state,
  picture,
  move,
  { disabled = false, fit = 'contain' } = {},
) {
  const view = picturePuzzleView(state);
  board.style.setProperty('--picture-columns', String(view.columns));
  board.style.setProperty('--picture-rows', String(view.rows));
  board.style.setProperty('--picture-ratio', String(view.columns / view.rows));
  board.dataset.complete = String(view.completed);
  const existing = new Map(
    [...board.children]
      .filter((node) => node.dataset.cell !== undefined)
      .map((node) => [node.dataset.cell, node]),
  );
  const desired = view.cells.map((cell, index) => {
    let button = existing.get(String(index));
    if (!button) {
      button = ctx.e('button', 'picture-cell');
      button.type = 'button';
      button.dataset.cell = String(index);
      button.addEventListener('click', () => move(Number(button.dataset.cell)));
    }
    button.disabled = disabled || cell.disabled;
    button.setAttribute('aria-label', cell.label);
    button.dataset.empty = String(cell.value === 0);
    button.dataset.value = String(cell.value);
    button.replaceChildren(pictureTileArtwork(ctx, view, cell, picture, fit));
    if (cell.value && !view.completed) {
      const badge = ctx.e('span', 'picture-number', String(cell.value));
      badge.setAttribute('aria-hidden', 'true');
      button.append(badge);
    }
    return button;
  });
  const retained = new Set(desired);
  for (const node of [...board.children]) if (!retained.has(node)) node.remove();
  let next = board.firstChild;
  for (const node of desired) {
    if (node !== next) board.insertBefore(node, next);
    next = node.nextSibling;
  }
  return view;
}
